import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { Delete, Lock, Store, UserCheck } from "lucide-react";
import { toast } from "sonner";
import DeviceApprovalScreen from "./DeviceApprovalScreen";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"];

export default function LoginScreen() {
  const { login } = useAuth();
  const [pin, setPin] = useState("");
  const [tenantCode, setTenantCode] = useState(
    () => localStorage.getItem("tenant_code") || ""
  );
  const [busy, setBusy] = useState(false);
  // First-time users MUST enter tenant code. Only hide the input if we
  // already have a stored tenant (i.e. this device has logged in before).
  const [showTenantField, setShowTenantField] = useState(() => true);
  const isFirstTime = !localStorage.getItem("tenant_code");
  // When the backend rejects a login because of Device Trust, we swap
  // the whole screen for the approval flow (keypad or QR scan).
  const [devicePayload, setDevicePayload] = useState(null);
  // 2026-02: Ο χειριστής είναι ήδη online → δείχνουμε confirm dialog
  // που ρωτάει αν θέλει να διώξει τον προηγούμενο και να μπει.
  // ``{pin, tenantCode, message, user_name, since, ip}``
  const [conflictPayload, setConflictPayload] = useState(null);

  const _extractDeviceChallenge = (e) => {
    // FastAPI wraps our dict detail as {detail: {...}}; the axios
    // response normaliser flattens it — but the raw form is preserved
    // in ``detail_raw`` for us.
    const d = e?.response?.data;
    if (e?.response?.status !== 403 || !d) return null;
    const raw = d.detail_raw || d.detail;
    if (raw && typeof raw === "object" && raw.code === "device_not_trusted") return raw;
    return null;
  };

  const _extractSessionConflict = (e) => {
    if (e?.response?.status !== 409) return null;
    const d = e.response?.data;
    const raw = d?.detail_raw || d?.detail;
    if (raw && typeof raw === "object" && raw.code === "already_logged_in") return raw;
    return null;
  };

  const submit = async (val) => {
    setBusy(true);
    try {
      await login(val ?? pin, tenantCode.trim() || undefined);
      // page reloads via parent observer
      return true;
    } catch (e) {
      const challenge = _extractDeviceChallenge(e);
      if (challenge) {
        setDevicePayload(challenge);
        setPin("");
        return false;
      }
      // 409 → operator already has an open session on another device.
      // Instead of a hard error, show a confirm dialog offering the
      // «Δίωξη προηγούμενης συνεδρίας» flow (force-login).
      const conflict = _extractSessionConflict(e);
      if (conflict) {
        setConflictPayload({
          pin: val ?? pin,
          tenantCode: tenantCode.trim() || undefined,
          ...conflict,
        });
        setPin("");
        return false;
      }
      const msg = e.response?.data?.detail || "Λάθος PIN";
      toast.error(typeof msg === "string" ? msg : "Λάθος PIN");
      setPin("");
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Silent variant used by the "try 4-digit first, then extend to 8" flow.
  // Returns true if login succeeded; on failure it does NOT clear the pin
  // nor show a toast, so the user can keep typing the remaining digits.
  // NOTE: a Device-Trust 403 IS surfaced (we swap screens) — that's a
  // real state change the operator has to see even mid-typing.
  // NOTE 2: a 409 (already-logged-in) IS surfaced too — retyping digits
  // won't help the user, they need to close the other device.
  const trySubmitSilent = async (val) => {
    setBusy(true);
    try {
      await login(val, tenantCode.trim() || undefined);
      return true;
    } catch (e) {
      const challenge = _extractDeviceChallenge(e);
      if (challenge) {
        setDevicePayload(challenge);
        setPin("");
      } else {
        const conflict = _extractSessionConflict(e);
        if (conflict) {
          setConflictPayload({
            pin: val,
            tenantCode: tenantCode.trim() || undefined,
            ...conflict,
          });
          setPin("");
        }
      }
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Force-login handler: ο χρήστης επιβεβαίωσε στο confirm dialog.
  // Ξανακαλούμε /auth/login με ``force:true`` — ο server κλείνει τη
  // σύνοδο του προηγούμενου και μας εγκρίνει.
  const confirmForceLogin = async () => {
    if (!conflictPayload) return;
    const { pin: p, tenantCode: t } = conflictPayload;
    setBusy(true);
    try {
      await login(p, t, { force: true });
      setConflictPayload(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Αποτυχία force-login";
      toast.error(typeof msg === "string" ? msg : "Αποτυχία force-login");
      setConflictPayload(null);
    } finally {
      setBusy(false);
    }
  };

  // Hardware barcode scanners (USB HID, keyboard-emulation) type the
  // whole barcode in <30 ms and finish with Enter. We listen at the
  // window level so the operator doesn't have to click into any input —
  // just scan from anywhere on the login screen.
  useEffect(() => {
    const buf = { val: "", lastKeyAt: 0 };
    const handler = (e) => {
      const now = Date.now();
      const gap = now - (buf.lastKeyAt || 0);
      if (gap > 350) buf.val = "";
      buf.lastKeyAt = now;
      if (e.key === "Enter") {
        const s = buf.val;
        buf.val = "";
        // Operator-ID barcode format: «88888XXXX...» where XXXX...
        // is the 4-to-8-digit PIN. Strip the 88888 prefix and submit.
        const m = /^88888(\d{4,8})$/.exec(s);
        if (m && !busy) {
          if (!tenantCode.trim()) {
            toast.error("Συμπλήρωσε πρώτα τον κωδικό καταστήματος");
            return;
          }
          submit(m[1]);
        }
        return;
      }
      if (/^[0-9]$/.test(e.key)) {
        buf.val += e.key;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, tenantCode]);

  const press = async (k) => {
    if (busy) return;
    if (k === "C") {
      setPin("");
      return;
    }
    if (k === "back") {
      setPin((v) => v.slice(0, -1));
      return;
    }
    // 2026-02: Απαιτούμε κωδικό καταστήματος υποχρεωτικά. Δεν επιτρέπεται
    // πλέον σιωπηλή είσοδος στο DEMO tenant — ο χρήστης πρέπει να έχει
    // πληκτρολογήσει τον κωδικό του καταστήματος του πριν πληκτρολογήσει PIN.
    if (!tenantCode.trim()) {
      toast.error("Συμπλήρωσε πρώτα τον κωδικό καταστήματος");
      return;
    }
    const next = (pin + k).slice(0, 8);
    setPin(next);
    // Auto-submit strategy that supports BOTH 4-digit staff PINs and
    // longer manager PINs (e.g. the seeded 8-digit super_manager code):
    //   • At length 4 → try SILENTLY. If it works, we're in. If it
    //     fails, keep the digits on screen so the user can extend up
    //     to 8 without retyping. No error toast is shown yet.
    //   • At length 8 → submit for real, showing the error toast if
    //     login fails so the user gets clear feedback.
    if (next.length === 4) {
      const ok = await trySubmitSilent(next);
      if (!ok) {
        // Keep pin displayed; user may keep typing to reach 5-8 digits.
      }
    } else if (next.length === 8) {
      await submit(next);
    }
  };

  return (
    <>
      {devicePayload && (
        <DeviceApprovalScreen
          payload={devicePayload}
          onApproved={() => {
            setDevicePayload(null);
            setPin("");
            toast.success("Πληκτρολόγησε ξανά το PIN σου");
          }}
          onCancel={() => setDevicePayload(null)}
        />
      )}
      <div className="h-screen w-screen grid place-items-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="w-[420px] rounded-2xl shadow-2xl bg-white p-6">
        <div className="flex flex-col items-center gap-2 mb-3 -mt-2">
          <img
            src="/ponte-pos-logo.jpg"
            alt="Ponte Pos"
            className="w-64 h-64 object-contain"
          />
          <div className="text-[11px] text-slate-500 tracking-wider uppercase font-bold -mt-4">
            Σύνδεση Χρήστη
          </div>
        </div>

        {showTenantField ? (
          <div className={`rounded-xl px-4 py-3 mb-3 ${!tenantCode.trim() ? "bg-amber-50 ring-2 ring-amber-300" : "bg-slate-100"}`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-600 font-bold mb-1 flex items-center gap-1.5 justify-between">
              <div className="flex items-center gap-1.5">
                <Store size={12} /> Κωδικός Καταστήματος <span className="text-rose-600">*</span>
              </div>
              {!isFirstTime && !!tenantCode && (
                <button
                  type="button"
                  onClick={() => { setShowTenantField(false); }}
                  className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                  data-testid="login-hide-tenant"
                >
                  απόκρυψη
                </button>
              )}
            </div>
            <input
              type="text"
              value={tenantCode}
              onChange={(e) => setTenantCode(e.target.value.toUpperCase())}
              placeholder="π.χ. TAVERNA_KOSTAS"
              data-testid="login-tenant-code"
              disabled={busy}
              autoFocus={isFirstTime}
              className="w-full h-9 px-2 rounded-md bg-white border border-slate-300 text-base font-mono tracking-wider uppercase outline-none focus:border-blue-500"
            />
            {isFirstTime && !tenantCode.trim() && (
              <div className="text-[10px] text-amber-700 mt-1.5 leading-snug">
                ⚠ Απαιτείται. Συμπλήρωσέ τον κωδικό που έχεις λάβει από τον Ponte&nbsp;Pos.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center mb-3">
            <button
              type="button"
              onClick={() => setShowTenantField(true)}
              className="text-[11px] text-slate-500 hover:text-slate-700 underline"
              data-testid="login-show-tenant"
            >
              Αλλαγή καταστήματος ({tenantCode})
            </button>
          </div>
        )}

        <div className="rounded-xl bg-slate-100 px-4 py-3 mb-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1.5">
            <Lock size={12} /> PIN
          </div>
          <div className="h-10 flex items-center justify-center text-3xl font-mono tabular tracking-[0.5em] font-bold" data-testid="login-pin-display">
            {pin ? "•".repeat(pin.length) : <span className="text-slate-400 text-base font-normal tracking-normal">πληκτρολόγησε…</span>}
          </div>
        </div>

        <div className="text-center text-[10px] text-slate-500 mb-2 font-bold tracking-wider">
          …Ή ΣΚΑΝΑΡΕ ΤΟ BARCODE ΧΕΙΡΙΣΤΗ
        </div>

        <div className="grid grid-cols-3 gap-2">
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              disabled={busy}
              data-testid={`login-key-${k}`}
              className={`h-14 rounded-lg border-2 text-2xl font-bold font-mono active:scale-[0.96] transition-all ${
                k === "C"
                  ? "bg-red-600 border-red-700 text-white hover:bg-red-700"
                  : k === "back"
                  ? "bg-slate-200 border-slate-300 hover:bg-slate-300"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}
            >
              {k === "back" ? <Delete size={22} className="mx-auto" /> : k}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={busy || pin.length < 4}
          onClick={() => submit()}
          data-testid="login-submit"
          className="w-full mt-3 h-12 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {busy ? "Σύνδεση..." : "Σύνδεση"}
        </button>
        <div className="text-center text-[11px] text-slate-400 mt-3">
          PIN 4-8 ψηφίων (κανονικός χρήστης 4 · manager 8)
        </div>
      </div>
    </div>

    {/* 2026-02: Confirm «force-login» όταν χειριστής είναι ήδη online. */}
    <Dialog open={!!conflictPayload} onOpenChange={(v) => { if (!v) setConflictPayload(null); }}>
      <DialogContent className="max-w-md" data-testid="session-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <UserCheck size={22} className="text-amber-600" />
            Ο χειριστής είναι ONLINE
          </DialogTitle>
          <DialogDescription>
            <span className="block text-slate-700 text-base mt-2">
              {conflictPayload?.user_name
                ? <>Ο χειριστής <b>{conflictPayload.user_name}</b> είναι ήδη συνδεδεμένος σε άλλη συσκευή.</>
                : "Ο χειριστής είναι ήδη συνδεδεμένος σε άλλη συσκευή."}
            </span>
            {conflictPayload?.ip && (
              <span className="block text-xs text-slate-500 mt-2 font-mono">
                Ενεργή σύνοδος από: <b>{conflictPayload.ip}</b>
                {conflictPayload?.since && <> · από {new Date(conflictPayload.since).toLocaleTimeString("el-GR")}</>}
              </span>
            )}
            <span className="block text-slate-800 mt-4 font-bold">
              Θέλεις να κάνεις είσοδο παρόλα αυτά;
            </span>
            <span className="block text-xs text-slate-500 mt-1">
              Θα αποσυνδεθεί ο χειριστής από την άλλη συσκευή.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setConflictPayload(null)}
            data-testid="session-conflict-cancel"
          >
            Άκυρο
          </Button>
          <Button
            onClick={confirmForceLogin}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
            data-testid="session-conflict-force"
          >
            {busy ? "Σύνδεση..." : "Ναι, να μπω"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
